import { useNavigation } from '@app/hooks/useNavigation';
import { Header } from '@components/block/Header/Header';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarProvider,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
} from '@components/shadcn/sidebar';
import { useEffect, useState } from 'react';
import { Search, LayoutDashboard, ClipboardCheck, Download, FileText } from 'lucide-react';
import { Button } from '@components/shadcn/button';
import { cn } from '@app/utils/utils';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Dashboard } from './Dashboard/Dashboard';
import { Audit } from './Audit/Audit';
import { Download as DownloadPage } from './Download/Download';
import { Documents } from './Documents/Documents';

export function Dashboardv2() {
  const { navigation, setNavigation } = useNavigation();
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const [activeRoute, setActiveRoute] = useState('/dashboard');

  useEffect(() => {
    return () => setNavigation((nav) => void (nav.scope = undefined));
  }, []);

  useEffect(() => {
    if (navigation.scope !== 'dashboardv2') {
      setNavigation((nav) => void (nav.scope = 'dashboardv2'));
    }
  }, [navigation]);

  // update active route when location changes
  useEffect(() => {
    const pathname = location.pathname;
    const routeSegments = pathname.split('/');
    const currentRoute = routeSegments.length > 2 ? `/${routeSegments[routeSegments.length - 1]}` : '/dashboard';
    setActiveRoute(currentRoute);
  }, [location]);

  const navigateTo = (route: string) => {
    history.push(`${url}${route}`);
  };

  const showSidebar = true;

  return (
    <>
      <style>{`
        [data-mobile="true"][data-sidebar="sidebar"] {
          background-color: rgb(234, 234, 234) !important;
        }
      `}</style>
      <SidebarProvider
        defaultOpen={window.innerWidth > 768}
        style={{ '--sidebar': 'rgb(234, 234, 234)' } as React.CSSProperties}
        className="[&_[data-mobile='true']]:!bg-[rgb(234,234,234)]"
      >
        <div className="flex h-screen w-full overflow-hidden">
          {showSidebar && (
            <Sidebar
              collapsible="icon"
              className="h-full overflow-hidden relative"
              style={{ '--sidebar': 'rgb(234, 234, 234)' } as React.CSSProperties}
            >
              <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-gray-200"></div>
              <SidebarHeader className="px-2">
                <div className="flex items-center justify-between">
                  <SidebarTrigger className="flex-shrink-0" />
                  <Button variant="ghost" size="icon">
                    <Search className="h-5 w-5" />
                  </Button>
                </div>
              </SidebarHeader>
              <SidebarContent className="ml-[-2px]">
                <SidebarGroup>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigateTo('/dashboard')}
                        className={activeRoute === '/dashboard' ? 'font-medium' : ''}
                        data-active={activeRoute === '/dashboard'}
                      >
                        <LayoutDashboard
                          className={cn('h-5 w-5', activeRoute === '/dashboard' && 'text-primary-root')}
                        />
                        <span className={cn(activeRoute === '/dashboard' && 'text-primary-root')}>Dashboard</span>
                        {activeRoute === '/dashboard' && (
                          <div className="absolute right-0 top-1 bottom-1 w-1.5 bg-primary-root rounded-md mr-[-8px]"></div>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigateTo('/audit')}
                        className={activeRoute === '/audit' ? 'font-medium' : ''}
                        data-active={activeRoute === '/audit'}
                      >
                        <ClipboardCheck className={cn('h-5 w-5', activeRoute === '/audit' && 'text-primary-root')} />
                        <span className={cn(activeRoute === '/audit' && 'text-primary-root')}>Audit</span>
                        {activeRoute === '/audit' && (
                          <div className="absolute right-0 top-1 bottom-1 w-1.5 bg-primary-root rounded-md mr-[-8px]"></div>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigateTo('/download')}
                        className={activeRoute === '/download' ? 'font-medium' : ''}
                        data-active={activeRoute === '/download'}
                      >
                        <Download className={cn('h-5 w-5', activeRoute === '/download' && 'text-primary-root')} />
                        <span className={cn(activeRoute === '/download' && 'text-primary-root')}>Download</span>
                        {activeRoute === '/download' && (
                          <div className="absolute right-0 top-1 bottom-1 w-1.5 bg-primary-root rounded-md mr-[-8px]"></div>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigateTo('/documents')}
                        className={activeRoute === '/documents' ? 'font-medium' : ''}
                        data-active={activeRoute === '/documents'}
                      >
                        <FileText className={cn('h-5 w-5', activeRoute === '/documents' && 'text-primary-root')} />
                        <span className={cn(activeRoute === '/documents' && 'text-primary-root')}>Documents</span>
                        {activeRoute === '/documents' && (
                          <div className="absolute right-0 top-1 bottom-1 w-1.5 bg-primary-root rounded-md mr-[-8px]"></div>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroup>
              </SidebarContent>
              <SidebarFooter className="p-2 md:p-3">
                <div
                  className={cn(
                    'flex flex-col gap-2 text-xs m-2 md:m-4 ml-0 group-has-[[data-collapsible=icon]]/sidebar-wrapper:hidden w-[calc(var(--sidebar-width)_-_3rem)]'
                  )}
                >
                  <p className="text-gray-500">Ein Produkt der simply Legal GmbH</p>
                  <hr className="border-gray-200" />
                  <p>
                    Du brauchst mehr als nur eine Datenschutzerklärung? Buche jetzt{' '}
                    <a href="#" className="text-black font-bold hover:underline">
                      Dieter Basic
                    </a>{' '}
                    um schnell und einfach datenschutzkonform zu werden.
                  </p>
                </div>
              </SidebarFooter>
            </Sidebar>
          )}

          <main className="flex-1 overflow-auto p-0 sm:p-2 md:p-3 xl:p-4 2xl:p-4 pt-0">
            <div className="pb-1 md:pt-1 xl:pt-3 xl:pb-3 2xl:pb-6 pt-6 2xl:pt-6">
              <Header v2Mode={true} sidebarTriggerComponent={<SidebarTrigger className="flex-shrink-0 pr-0" />} />
            </div>
            <Switch>
              <Route path={`${path}/dashboard`}>
                <Dashboard />
              </Route>
              <Route path={`${path}/audit`}>
                <Audit />
              </Route>
              <Route path={`${path}/download`}>
                <DownloadPage />
              </Route>
              <Route path={`${path}/documents`}>
                <Documents />
              </Route>
              <Route exact path={path}>
                <Dashboard />
              </Route>
            </Switch>
          </main>
        </div>
      </SidebarProvider>
    </>
  );
}
